import React from "react";

export default function JobPhotosApiDoc() {
  return (
    <div className="text-gray-800 text-md leading-7 px-4 min-w-0 max-w-[40rem] lg:mt-0 lg:mr-0 lg:max-w-[50rem]">
      {/* Header */}
      <header className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-white to-transparent" />
        <div className="relative p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Jobs API — Job Photos
          </h1>
          <p className="mt-3 max-w-3xl text-base sm:text-lg leading-7 text-slate-600">
            Retrieve, upload, and delete photos associated with a job. These
            endpoints require authentication and are intended for authorized
            customer users, including Portside integration users when the job
            belongs to the Portside integration flow.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                List photos
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">GET</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Upload photos
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">POST</div>
            </div>
          </div>
        </div>
      </header>

      <main className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
        {/* Overview */}
        <section>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Overview
          </h2>
          <p className="mt-3 leading-7 text-slate-700">
            Use these endpoints to manage job photos after a job has been
            created. Portside users can use them to:
          </p>
          <ul className="mt-3 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
            <li>Fetch the existing photo gallery for a job.</li>
            <li>Upload one or more photos to a job.</li>
          </ul>

          <div className="mt-6 rounded-2xl border border-sky-200 bg-sky-50 p-5">
            <div className="text-sm font-semibold text-sky-900">
              Portside-focused note
            </div>
            <p className="mt-2 text-sm leading-6 text-sky-900">
              This documentation is intentionally focused on customer-facing
              usage. It avoids internal operational details and describes only
              the fields and behaviors relevant to external integrations.
            </p>
          </div>
        </section>

        {/* Fetch photos */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            1. Fetch job photos
          </h2>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">HTTP</div>
            <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
              <li>
                <span className="font-semibold">Method:</span>{" "}
                <code className="font-mono">GET</code>
              </li>
              <li>
                <span className="font-semibold">URL:</span>{" "}
                <code className="font-mono">/api/job-photos/{`{jobId}`}/</code>
              </li>
              <li>
                <span className="font-semibold">Headers:</span>
                <br />
                <code className="font-mono">Authorization: JWT your_token</code>
              </li>
            </ul>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">
              Path parameters
            </div>
            <ul className="mt-2 space-y-2 text-sm leading-6 text-slate-700">
              <li>
                <code className="font-mono">jobId</code> —{" "}
                <span className="font-mono">number</span>
                <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                  required
                </span>{" "}
                The ID of the job whose photos you want to retrieve.
              </li>
            </ul>
          </div>

          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
            <div className="text-sm font-semibold text-amber-900">
              Response shape
            </div>
            <p className="mt-2 text-sm leading-6 text-amber-900">
              This endpoint is paginated. The response is returned through the
              standard pagination wrapper used by the API.
            </p>
          </div>

          <div className="mt-6 not-prose overflow-hidden rounded-2xl bg-zinc-900 shadow-md">
            <div className="flex min-h-[49px] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4">
              <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                Request
              </h3>
            </div>
            <div className="flex h-9 items-center gap-2 border-b border-zinc-700 px-4">
              <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-400">
                GET
              </span>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500" />
              <span className="font-mono text-xs text-zinc-400">
                /api/job-photos/{`{jobId}`}/
              </span>
            </div>
            <div className="pb-3">
              <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                {`curl -X GET https://api.livetakeoff.com/api/job-photos/9876/ \\
  -H "Authorization: JWT YOUR_ACCESS_TOKEN"`}
              </pre>
            </div>
          </div>

          <div className="mt-6 not-prose overflow-hidden rounded-2xl bg-zinc-900 shadow-md">
            <div className="flex min-h-[49px] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4">
              <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                Response
              </h3>
            </div>
            <div className="flex h-9 items-center gap-2 border-b border-zinc-700 px-4">
              <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-400">
                JSON
              </span>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500" />
              <span className="font-mono text-xs text-zinc-400">
                application/json
              </span>
            </div>
            <div className="pb-3">
              <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                {`{
  "count": 2,
  "next": null,
  "previous": null,
  "results": [
    {
      "id": 145,
      "name": "N123AB_KFXE_2026-03-10_0.jpg",
      "image": "https://res.cloudinary.com/.../image/upload/....jpg",
      "size": 483920,
      "interior": true,
      "customer_uploaded": true,
      "created_at": "03/10/2026"
    },
    {
      "id": 146,
      "name": "N123AB_KFXE_2026-03-10_1.jpg",
      "image": "https://res.cloudinary.com/.../image/upload/....jpg",
      "size": 512230,
      "interior": false,
      "customer_uploaded": true,
      "created_at": "03/10/2026"
    }
  ]
}`}
              </pre>
            </div>
          </div>
        </section>

        {/* Upload photos */}
        <section className="mt-12">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            2. Upload job photos
          </h2>

          <p className="mt-3 leading-7 text-slate-700">
            Upload one or more photos to a job using{" "}
            <code className="font-mono">multipart/form-data</code>.
          </p>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">HTTP</div>
            <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
              <li>
                <span className="font-semibold">Method:</span>{" "}
                <code className="font-mono">POST</code>
              </li>
              <li>
                <span className="font-semibold">URL:</span>{" "}
                <code className="font-mono">
                  /api/job-photos/upload/{`{jobId}`}/
                </code>
              </li>
              <li>
                <span className="font-semibold">Headers:</span>
                <br />
                <code className="font-mono">Authorization: JWT your_token</code>
                <br />
                <code className="font-mono">
                  Content-Type: multipart/form-data
                </code>
              </li>
            </ul>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">
              Body fields (multipart/form-data)
            </div>
            <ul className="mt-2 space-y-2 text-sm leading-6 text-slate-700">
              <li>
                <code className="font-mono">photo</code> —{" "}
                <span className="font-mono">file</span>
                <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                  required
                </span>{" "}
                One or more files. Repeat this field for each image you upload.
              </li>
              <li>
                <code className="font-mono">is_interior</code> —{" "}
                <span className="font-mono">"true"|"false"</span>
                <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                  optional
                </span>{" "}
                Marks uploaded photos as interior photos.
              </li>
              <li>
                <code className="font-mono">is_exterior</code> —{" "}
                <span className="font-mono">"true"|"false"</span>
                <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                  optional
                </span>{" "}
                Marks uploaded photos as exterior photos.
              </li>
              <li>
                <code className="font-mono">is_customer</code> —{" "}
                <span className="font-mono">"true"|"false"</span>
                <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                  optional
                </span>{" "}
                Identifies the upload as customer-uploaded.
              </li>
            </ul>
          </div>

          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
            <div className="text-sm font-semibold text-amber-900">
              Upload limit
            </div>
            <p className="mt-2 text-sm leading-6 text-amber-900">
              A job can have a maximum of{" "}
              <span className="font-semibold">25 photos</span>. If the upload
              would exceed that limit, the API returns{" "}
              <code className="font-mono">406 Not Acceptable</code>.
            </p>
          </div>

          <div className="mt-6 not-prose overflow-hidden rounded-2xl bg-zinc-900 shadow-md">
            <div className="flex min-h-[49px] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4">
              <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                Request
              </h3>
            </div>
            <div className="flex h-9 items-center gap-2 border-b border-zinc-700 px-4">
              <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-400">
                POST
              </span>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500" />
              <span className="font-mono text-xs text-zinc-400">
                /api/job-photos/upload/{`{jobId}`}/
              </span>
            </div>
            <div className="pb-3">
              <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                {`curl -X POST https://api.livetakeoff.com/api/job-photos/upload/9876/ \\
  -H "Authorization: JWT YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: multipart/form-data" \\
  -F "is_customer=true" \\
  -F "is_interior=true" \\
  -F "photo=@/path/photo1.jpg" \\
  -F "photo=@/path/photo2.jpg"`}
              </pre>
            </div>
          </div>

          <div className="mt-6 not-prose overflow-hidden rounded-2xl bg-zinc-900 shadow-md">
            <div className="flex min-h-[49px] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4">
              <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                Request
              </h3>
            </div>
            <div className="flex h-9 items-center gap-2 border-b border-zinc-700 px-4">
              <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-400">
                POST
              </span>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500" />
              <span className="font-mono text-xs text-zinc-400">
                /api/jobs/{`{jobId}`}/photos/upload/
              </span>
            </div>
            <div className="pb-3">
              <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                {`const fd = new FormData();
fd.append("is_customer", "true");
fd.append("is_interior", "false");
fd.append("is_exterior", "true");

for (const file of selectedFiles) {
  fd.append("photo", file);
}

const res = await fetch("https://api.livetakeoff.com/api/jobs/9876/photos/upload/", {
  method: "POST",
  headers: {
    Authorization: "JWT " + token
  },
  body: fd
});

if (!res.ok) throw new Error("Failed to upload photos");
const data = await res.json();`}
              </pre>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">
              201 Created
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-700">
              On success, the endpoint returns the number of uploaded photos.
            </p>

            <div className="mt-4 not-prose overflow-hidden rounded-2xl bg-zinc-900 shadow-md">
              <div className="flex min-h-[49px] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4">
                <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                  Response
                </h3>
              </div>
              <div className="flex h-9 items-center gap-2 border-b border-zinc-700 px-4">
                <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-400">
                  JSON
                </span>
                <span className="h-0.5 w-0.5 rounded-full bg-zinc-500" />
                <span className="font-mono text-xs text-zinc-400">
                  application/json
                </span>
              </div>
              <div className="pb-3">
                <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                  {`{
  "uploaded_photos": 2
}`}
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* Errors */}
        <section className="mt-12">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Errors
          </h2>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <ul className="space-y-2 text-sm leading-6 text-slate-700">
              <li>
                <span className="font-mono">403</span> — you do not have
                permission to view, upload, or delete photos for this job.
              </li>
              <li>
                <span className="font-mono">404</span> — job not found.
              </li>
              <li>
                <span className="font-mono">406</span> — the upload would exceed
                the maximum allowed number of photos for the job.
              </li>
            </ul>

            <div className="mt-4 not-prose overflow-hidden rounded-2xl bg-zinc-900 shadow-md">
              <div className="flex min-h-[49px] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4">
                <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                  Response
                </h3>
              </div>
              <div className="flex h-9 items-center gap-2 border-b border-zinc-700 px-4">
                <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-400">
                  JSON
                </span>
                <span className="h-0.5 w-0.5 rounded-full bg-zinc-500" />
                <span className="font-mono text-xs text-zinc-400">
                  application/json
                </span>
              </div>
              <div className="pb-3">
                <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                  {`{
  "error": "There is already 25 photos associated with this job"
}`}
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* Rules */}
        <section className="mt-12">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Validation & rules
          </h2>
          <ul className="mt-3 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
            <li>
              Use <code className="font-mono">GET</code> to fetch the current
              photo list for a job.
            </li>
            <li>
              Use <code className="font-mono">POST</code> with{" "}
              <code className="font-mono">multipart/form-data</code> to upload
              photos.
            </li>
            <li>
              Repeat the <code className="font-mono">photo</code> field for each
              image file you want to upload.
            </li>
            <li>
              Use <code className="font-mono">POST</code> to the photos endpoint
              with a <code className="font-mono">photos</code> array to delete
              existing photos.
            </li>
            <li>
              A single job can store up to{" "}
              <span className="font-semibold">25 photos</span>.
            </li>
            <li>
              Portside users can access these endpoints only for jobs they are
              authorized to view.
            </li>
          </ul>
        </section>
      </main>
    </div>
  );
}
